/**
 * 캠페인에 링크를 강제로 추가하는 절차를 조립한다 — 검증 · ES 확인 · Firestore 쓰기 순서.
 *
 * ⚠️ **저장소를 직접 치지 않는다.** ES는 `actions/els/moment`(서버 op), Firestore는
 * `actions/firestore/sdk/campaign-forced-addition`(캠페인 문서)과
 * `actions/firestore/api/collection-request-api`(수집 요청 큐)가 맡는다. 이 파일이 아는 것은
 * "언제 무엇을 쓰는가"뿐이다.
 */

import { log } from 'src/utils/logger';
import { campaignLinkIndexCandidates } from 'src/utils/campaign/campaign-link-identity';
import {
  sanitizeCampaignLink,
  transformForcedAdditionLinksForRedirect,
} from 'src/utils/campaign/campaign-link-utils';

import { fsCall } from 'src/actions/firestore/dispatch';
import { fetchMomentLinkExists } from 'src/actions/els/moment';

const SERVICE_NAME = 'utils/campaign-forced-addition-client';

const SUPPORTED_CHANNEL_HOST_TOKENS = [
  'youtube.com',
  'youtu.be',
  'instagram.com',
  'tiktok.com',
  'vt.tiktok.com',
  'x.com',
  'twitter.com',
  'threads.net',
  'threads.com',
];

/**
 * 수집 요청 큐에 실을 링크 — 캠페인 링크 정규화(`sanitizeCampaignLink`) 그대로다.
 * ⚠️ 쿼리를 따로 떼지 않는다 — 페이스북 `watch?v=`·`permalink.php?story_fbid=` 와 네이버 카페 `ArticleRead.nhn?articleid=` 는
 *    글 id 가 쿼리에만 있어, 떼면 수집기가 받을 글이 없다. 서버(`collectionRequest.links.enqueue`)는 받은 값을 그대로 쌓는다.
 */
function toRequestLink(link: unknown): string {
  return sanitizeCampaignLink(String(link ?? ''));
}

function isSupportedChannelLink(link: unknown): boolean {
  const raw = String(link ?? '').trim().toLowerCase();
  if (!raw) return false;

  try {
    const url = /^https?:\/\//i.test(raw) ? new URL(raw) : new URL(`https://${raw}`);
    const hostname = String(url.hostname || '').toLowerCase();

    const isHostSupported = SUPPORTED_CHANNEL_HOST_TOKENS.some(
      (token) => hostname === token || hostname.endsWith(`.${token}`)
    );
    if (!isHostSupported) return false;

    const isInstagramHost =
      hostname === 'instagram.com' || hostname.endsWith('.instagram.com');
    if (isInstagramHost) {
      const segments = String(url.pathname || '')
        .split('/')
        .map((s: string) => s.trim())
        .filter(Boolean);

      const isPostPath = (value: string) =>
        value === 'p' || value === 'reel' || value === 'reels' || value === 'tv';

      let shortcode = '';
      if (segments.length >= 2 && isPostPath(segments[0])) shortcode = segments[1];
      else if (segments.length >= 3 && isPostPath(segments[1])) shortcode = segments[2];

      return Boolean(shortcode);
    }

    return true;
  } catch {
    return false;
  }
}

/** 받아 줄 채널의 링크와 아닌 것으로 나눈다(인스타그램은 게시물 링크만) */
export function splitSupportedLinks(links: unknown): { supported: string[]; unsupported: string[] } {
  const list = Array.isArray(links) ? links : [];
  const supported: string[] = [];
  const unsupported: string[] = [];
  list.forEach((link: string) => {
    if (isSupportedChannelLink(link)) supported.push(link);
    else unsupported.push(link);
  });
  return { supported, unsupported };
}

/**
 * 링크가 색인에 있나 — 등록 전 확인.
 *
 * ⚠️ **없는 링크가 곧 수집 요청**이 된다. 그래서 판정을 놓치면 헛 요청이 나간다 —
 * `link.keyword`·`link` 두 표기를 다 보는 규칙은 `moment.linkExists` 가 그대로 갖고 있다.
 *
 * ⚠️ 목록을 **한 번에** 넘기고 링크별 판정을 받는다 — 링크마다 따로 치지 마라.
 * 링크마다 색인 표기(`campaignLinkIndexCandidates` — 쇼츠·youtu.be → `watch?v=` 등)까지 펼쳐 묻고,
 * 그중 하나라도 있으면 그 링크는 있다.
 * @returns 넘긴 링크 → 있나
 */
async function resolveLinksInElastic(links: string[]): Promise<Record<string, boolean>> {
  const candidatesByLink = new Map(links.map((link) => [link, campaignLinkIndexCandidates(link)] as const));
  const asked = Array.from(new Set(Array.from(candidatesByLink.values()).flat()));
  const { existsByLink } = await fetchMomentLinkExists({ links: asked });
  const result: Record<string, boolean> = {};
  candidatesByLink.forEach((candidates, link) => {
    result[link] = candidates.some((candidate) => existsByLink[candidate] === true);
  });
  return result;
}

/** 포스트 객체의 원문 링크(`link` → `url` → `permalink`, trim 만) — 원문 지도에 실을 값이다 */
export function getPostRawLinkFromPost(post: any): string {
  return String(post?.link || post?.url || post?.permalink || '').trim();
}

/**
 * 포스트 객체에서 캠페인 강제추가용 링크를 추출합니다.
 * @param {Object} post
 * @returns {string}
 */
export function getPostLinkFromPost(post: any): string {
  return sanitizeCampaignLink(getPostRawLinkFromPost(post));
}

/** 원문 한 개의 길이 상한 — 서버(`campaign.forcedLinks.*`)가 넘으면 400 으로 거른다 */
const RAW_LINK_MAX_LENGTH = 2048;

/**
 * 원문 지도 `{ 정규화 링크: 원문 }` 를 만든다 — 키는 `sanitizeCampaignLink(link)` 이고 `keep` 안의 것만 담는다.
 * 같은 키는 뒤 원문이 이긴다. 원문이 비었거나 키와 같거나 2048자를 넘으면 담지 않는다.
 * @param pairs 링크(정규화 전이어도 된다)와 사용자가 넣은 원문
 */
export function buildCampaignLinkRawMap(
  pairs: Iterable<{ link: string; raw: string | null | undefined }>,
  keep: ReadonlySet<string>
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const { link, raw } of pairs) {
    const key = sanitizeCampaignLink(String(link || ''));
    const value = String(raw ?? '').trim();
    if (!key || !keep.has(key)) continue;
    if (value === '' || value === key || value.length > RAW_LINK_MAX_LENGTH) delete out[key];
    else out[key] = value;
  }
  return out;
}

/**
 * 기존 캠페인 forcedAdditions 배열에 링크를 추가합니다.
 *
 * ⚠️ `workspaceId` 는 **수집 요청 큐가 소속을 묻는 대상**이다.
 * 큐(`CatchMomentsRequests/link/requestList`)는 워크스페이스 밖의 전역 컬렉션이라, 서버가
 * 「누가 이 큐를 칠 수 있는가」를 판정할 기준이 이 값 말고는 없다.
 *
 * 넘긴 링크(사용자·글이 가진 원문)가 정규화 값과 다르면 원문 지도(`rawByLink`)로 함께 싣는다.
 *
 * @param {string} campaignId
 * @param {string[]} links 원문 링크 — 정규화는 여기서 한다
 * @param {string} workspaceId
 * @returns {Promise<{ ok: boolean, addedCount?: number, requestedCount?: number, error?: Error }>}
 */
export async function addPostLinksToCampaignForcedAdditions(
  campaignId: string,
  links: string[],
  workspaceId: string
) {
  const id = String(campaignId || '').trim();
  if (!id) {
    return { ok: false, error: new Error('campaignId is required') };
  }

  // 원문이 판정에 걸려도 정규화한 모양이 받히면 받는다
  const list = (Array.isArray(links) ? links : []).map((link) => String(link ?? '').trim());
  const supported = list.filter((link) => isSupportedChannelLink(link) || isSupportedChannelLink(sanitizeCampaignLink(link)));
  if (supported.length < list.length) {
    return {
      ok: false,
      error: new Error(
        '지원하지 않는 채널입니다. (youtube, instagram, tiktok, x, threads)'
      ),
    };
  }

  const values = Array.from(
    new Set(
      supported
        .map((link) => sanitizeCampaignLink(link))
        .filter(Boolean)
    )
  );

  if (values.length === 0) {
    return { ok: false, error: new Error('유효한 링크가 없습니다.') };
  }

  try {
    // ⚠️ `values` 는 이미 `sanitizeCampaignLink` 를 거친 값이다
    //    (정규화는 화면 몫이라 경계 위에 둔다)
    const existsByLink = await resolveLinksInElastic(values);
    const valuesToRequest = values.filter((link) => existsByLink[link] !== true);

    if (!(await fsCall('campaign.exists', { campaignId: id }))) {
      return { ok: false, error: new Error('캠페인을 찾을 수 없습니다.') };
    }

    const rawByLink = buildCampaignLinkRawMap(
      supported.map((link) => ({ link, raw: link })),
      new Set(values)
    );
    await fsCall('campaign.forcedLinks.append', {
      campaignId: id,
      links: values,
      ...(Object.keys(rawByLink).length > 0 ? { rawByLink } : {}),
    });

    if (valuesToRequest.length > 0) {
      const normalizedToRequest = valuesToRequest
        .map((v) => toRequestLink(v))
        .filter(Boolean);

      await fsCall('collectionRequest.links.enqueue', {
        workspaceId,
        links: normalizedToRequest,
      });
    }

    return {
      ok: true,
      addedCount: values.length,
      requestedCount: valuesToRequest.length,
    };
  } catch (error) {
    const err = error as any;
    log.fatal(SERVICE_NAME, 'addPostLinksToCampaignForcedAdditions 실패', {
      err: err?.message || String(error),
      stack: err?.stack,
      campaignId: id,
    });
    return { ok: false, error };
  }
}

/**
 * 캠페인의 강제추가 링크를 **이 목록으로 맞춘다** — 캠페인 설정의 링크 칸이 이 통로다.
 *
 * 목록이 저장된 것과 같으면 아무것도 쓰지 않는다. 다르면 통째로 치환하고(`forcedLinks.replace`),
 * 새로 들어온 링크 가운데 색인에 없는 것만 수집 요청 큐에 넣는다.
 *
 * 원문을 아는 링크는 원문 지도(`rawByLink`)로 함께 싣는다 — 목록이 같아 쓰지 않을 때는 싣지 않는다.
 *
 * @param nextLinks  남길 링크(정규화 전이어도 된다)
 * @param prevLinks  저장돼 있던 링크 — 새 링크를 가리는 기준
 * @param rawByLink  `{ 링크: 사용자가 넣은 원문 }` — 키는 정규화 전이어도 된다 · 원문을 모르는 링크는 빼고 넘긴다
 *                   (서버가 저장된 원문을 유지한다)
 * @returns `requestedCount` = 수집 요청을 건 링크 수
 * @throws 쓰기 실패
 */
export async function syncCampaignForcedLinks({
  campaignId,
  workspaceId,
  nextLinks,
  prevLinks,
  rawByLink: rawInput,
}: {
  campaignId: string;
  workspaceId: string;
  nextLinks: readonly string[];
  prevLinks: readonly string[];
  rawByLink?: Readonly<Record<string, string>>;
}): Promise<{ changed: boolean; requestedCount: number }> {
  const toSet = (list: readonly string[]) => Array.from(
    new Set(list.map((link) => sanitizeCampaignLink(String(link || ''))).filter(Boolean))
  );
  const next = toSet(nextLinks);
  const prev = new Set(toSet(prevLinks));
  const same = next.length === prev.size && next.every((link) => prev.has(link));
  if (same) return { changed: false, requestedCount: 0 };

  const redirect = await transformForcedAdditionLinksForRedirect(next);
  const redirectLinks = Array.isArray(redirect) ? redirect.filter(Boolean) : [];
  const rawByLink = buildCampaignLinkRawMap(
    Object.entries(rawInput ?? {}).map(([link, raw]) => ({ link, raw })),
    new Set(next)
  );
  await fsCall('campaign.forcedLinks.replace', {
    campaignId,
    links: next,
    redirectLinks,
    ...(Object.keys(rawByLink).length > 0 ? { rawByLink } : {}),
  });

  const added = next.filter((link) => !prev.has(link));
  if (added.length === 0) return { changed: true, requestedCount: 0 };

  const existsByLink = await resolveLinksInElastic(added);
  const toRequest = added
    .filter((link) => existsByLink[link] !== true)
    .map((link) => toRequestLink(link))
    .filter(Boolean);
  if (toRequest.length > 0) {
    await fsCall('collectionRequest.links.enqueue', { workspaceId, links: toRequest });
  }
  return { changed: true, requestedCount: toRequest.length };
}
